"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";

import "../../marken/marken.css";

import { TopSectionMarken } from "../../marken/sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";

import höchstenPreis from "@/assets/auto-verkaufen/Frau-verkauft-ihr-auto.webp";
import BannerImage2 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig2.avif";
import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";

import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";
import { fragenWelcheDokumenteBraucheIchZumAutoVerkauf } from "./data";

// Ratgeber SERVERSEITIG rendern (dynamic OHNE die SSR-Abschaltung, Playbook 2.6):
// die sechs "Mehr zum Thema"-Kacheln verlinken u. a. /auto-verkaufen.
const Ratgeber = dynamic(() =>
  import("@/components/Ratgeber/Ratgeber").then((m) => m.default)
);

// Only needed when the user reaches step 12; avoid loading auth-related JS/CSS on initial paint.
const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function WelcheDokumenteBraucheIchZumAutoVerkauf() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionMarken
        title="Auto verkaufen: Diese Dokumente brauchst du – umfassender Leitfaden"
        link="/guides/welche-dokumente-brauche-ich-zum-auto-verkauf"
        linkname="Dokumente Auto verkaufen"
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*{" "}
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung.
          </>
        }
        removePadding={true}
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Wie muss ich vorgehen, wenn ich mein Auto verkaufen will?"
              leftText={
                <>
                  <p>
                    Ein Auto zu verkaufen ist mit einigen Formalitäten
                    verbunden. Um den Verkauf reibungslos abzuwickeln und dem
                    Käufer eine problemlose Zulassung zu ermöglichen, musst du
                    die richtigen Unterlagen vorweisen können. Dieser umfangreiche
                    Leitfaden erklärt dir im Detail, welche Papiere du benötigst,
                    warum sie wichtig sind und wie du vorgehst, wenn Dokumente
                    verloren gegangen sind.
                  </p>
                </>
              }
              rightFirstTitle="Warum Dokumente wichtig sind"
              rightFirstText={
                <>
                  <p>
                    Die Fahrzeugpapiere dienen dem Nachweis über Eigentum,
                    Zulassung, technische Sicherheit und Steuerpflicht. Ohne sie
                    kann der Käufer das Fahrzeug nicht anmelden, und du riskierst,
                    dass der Verkauf nicht rechtswirksam ist. Zudem schafft eine
                    vollständige Dokumentation Vertrauen – ein gut sortierter
                    Ordner mit Rechnungen, Prüfberichten und Serviceheften zeigt,
                    dass das Auto gepflegt wurde.
                  </p>

                  <h3>Essenzielle Dokumente im Detail</h3>
                  <ul className="custom-unordered-list">
                    <li>
                      <strong>
                        Zulassungsbescheinigung Teil I (Fahrzeugschein):
                      </strong>
                      <ul className="custom-unordered-list">
                        <li>
                          Führt Informationen wie Kennzeichen, Halteranschrift
                          und technische Daten.
                        </li>
                        <li>
                          Muss beim Verkauf übergeben werden, wenn das Auto noch
                          zugelassen ist.
                        </li>
                        <li>
                          <strong>Bei Verlust:</strong> Du musst ein
                          Ersatzdokument bei der Zulassungsbehörde beantragen.
                          Dazu brauchst du eine Verlusterklärung und ggf. eine
                          eidesstattliche Versicherung.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>
                        Zulassungsbescheinigung Teil II (Fahrzeugbrief):
                      </strong>
                      <ul className="custom-unordered-list">
                        <li>Belegt das Eigentum am Fahrzeug.</li>
                        <li>
                          Darf bei Finanzierung oft erst nach Ablösung übergeben
                          werden.
                        </li>
                        <li>
                          <strong>Bei Verlust:</strong> Ersatz nur über die
                          Zulassungsstelle nach Vorlage eines aktuellen
                          TÜV-Berichts und eines Unbedenklichkeitsnachweises.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>
                        Prüfbericht der letzten Hauptuntersuchung (Pickerl/TÜV):
                      </strong>
                      <ul className="custom-unordered-list">
                        <li>Bestätigt die Verkehrstüchtigkeit.</li>
                        <li>
                          In Österreich gilt die 3-2-1-Regel (erste Untersuchung
                          nach 3 Jahren, dann nach 2 Jahren, danach jährlich).
                        </li>
                        <li>
                          Ohne gültigen Prüfbericht kann der Käufer das Auto nicht
                          zulassen.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Serviceheft und Wartungsnachweise:</strong>
                      <ul className="custom-unordered-list">
                        <li>
                          Dient als Beleg, dass alle vorgeschriebenen Wartungen
                          durchgeführt wurden.
                        </li>
                        <li>
                          Erhöht den Wert des Fahrzeugs, da es die Zuverlässigkeit
                          dokumentiert.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Reparaturrechnungen:</strong>
                      <ul className="custom-unordered-list">
                        <li>
                          Besonders wichtig bei größeren Reparaturen wie Motor
                          oder Getriebe.
                        </li>
                        <li>Zeigt, dass Schäden fachgerecht behoben wurden.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Betriebserlaubnis oder COC-Papiere:</strong>
                      <ul className="custom-unordered-list">
                        <li>
                          Die Allgemeine Betriebserlaubnis (ABE) oder das
                          Certificate of Conformity (COC) belegt, dass das
                          Fahrzeug den gesetzlichen Vorschriften entspricht.
                        </li>
                        <li>
                          Bei importierten Fahrzeugen zwingend erforderlich, um
                          die Zulassung zu erhalten.
                        </li>
                        <li>
                          Bei Tuning-Teilen müssen Einzelabnahmen oder ABE
                          vorliegen.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Kaufvertrag:</strong>
                      <ul className="custom-unordered-list">
                        <li>Sollte immer schriftlich sein.</li>
                        <li>
                          Enthält Angaben zum Verkäufer, Käufer, Fahrzeug
                          (Fahrgestellnummer!), Kaufpreis, Zahlungsart und Datum
                          der Übergabe.
                        </li>
                        <li>
                          Regelt den Ausschluss der Sachmängelhaftung (bei
                          Privatverkauf) und benennt bekannte Mängel explizit.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Ummeldeformulare und Abmeldebescheinigung:</strong>
                      <ul className="custom-unordered-list">
                        <li>
                          Bei abgemeldeten Autos: Bestätigung der Zulassungsstelle,
                          dass das Fahrzeug nicht mehr angemeldet ist.
                        </li>
                        <li>Hilft dem Käufer, das Auto neu zuzulassen.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Alle Schlüssel und Codekarten:</strong>
                      <ul className="custom-unordered-list">
                        <li>
                          Moderne Fahrzeuge haben Transponder oder
                          Keyless-Systeme. Übergebe Haupt- und Ersatzschlüssel
                          sowie eventuelle PIN-Codes.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Vollmachten und Bescheinigungen:</strong>
                      <ul className="custom-unordered-list">
                        <li>
                          Falls du nicht selbst bei der Zulassungsstelle erscheinen
                          kannst, stelle eine Vollmacht aus.
                        </li>
                        <li>
                          Bei Firmenfahrzeugen benötigst du eine Handelsregister-
                          oder Gewerbeanmeldung.
                        </li>
                      </ul>
                    </li>
                  </ul>
                </>
              }
              rightSecondTitle="Was tun bei verlorenen Dokumenten?"
              rightSecondText={
                <>
                  <p>
                    Der Verlust wichtiger Papiere verzögert den Verkauf. So gehst
                    du vor:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>
                      <strong>Fahrzeugschein/Fahrzeugbrief verloren:</strong>{" "}
                      Erkläre den Verlust bei der Polizei und bei der
                      Zulassungsstelle. Beantrage ein Ersatzdokument. Das dauert
                      einige Tage bis Wochen.
                    </li>
                    <li>
                      <strong>Serviceheft verloren:</strong> Frage in der
                      Werkstatt nach, ob die Einträge digital vorliegen. Viele
                      Hersteller können ein Duplikat erstellen.
                    </li>
                    <li>
                      <strong>Kein ABE/COC:</strong> Kontaktiere den Hersteller
                      oder das Kraftfahrtbundesamt bzw. die österreichische
                      Zulassungsbehörde und fordere eine Kopie an.
                    </li>
                  </ul>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={BannerImage2}
                revert
                h2
                isBig
                fontSize="36px"
                title="Tipps für den Verkauf"
                alt="Dokumente vorbereiten fuer einen sicheren Autoverkauf"
                text={
                  <>
                    <ul className="custom-unordered-list secondary">
                      <li>
                        Erstelle eine Mappe mit sämtlichen Papieren und sortiere
                        sie chronologisch.
                      </li>
                      <li>
                        Mache digitale Kopien, um im Verlustfall einen Nachweis
                        zu haben.
                      </li>
                      <li>
                        Erwähne in Inseraten, dass alle Dokumente vorhanden sind –
                        das steigert das Vertrauen.
                      </li>
                      <li>
                        Vermeide Übergabe ohne Zahlungssicherheit, auch wenn der
                        Käufer glaubhaft wirkt. Nutze sichere Zahlungsmethoden.
                      </li>
                    </ul>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufig gestellte Fragen"
              fragen={fragenWelcheDokumenteBraucheIchZumAutoVerkauf}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Dokumente Auto Verkauf"
                kaputtesAuto
                unfallAuto
                autoExport
                autoBewerten
                autoVerkaufen
                listenpreisBerechnen
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={höchstenPreis}
                h2
                fontSize="36px"
                title="Fazit zum Thema Auto verkaufen ohne Dokumente"
                alt="Frau sitzt in einem Auto und nutzt ihr Smartphone, um mit Autoeinfachlos ihr Auto sicher zu verkaufen."
                text={
                  <>
                    <p>
                      Eine vollständige Dokumentation ist das A und O beim
                      Autoverkauf. Sie schafft Vertrauen, beschleunigt die
                      Zulassung und schützt dich vor späteren Haftungsansprüchen.
                      Mit gut sortierten Papieren überzeugst du Käufer leichter
                      und erzielst einen höheren Preis. Autoeinfachlos hilft dir,
                      den Dokumentencheck durchzuführen und den Verkauf sicher
                      abzuwickeln.
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-verkaufen"
            title="Jetzt Auto kostenlos bewerten & sicher verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default WelcheDokumenteBraucheIchZumAutoVerkauf;
